'use client';

import InfoIcon from '@mui/icons-material/Info';
import SyncIcon from '@mui/icons-material/Sync';
import { AlertColor, Autocomplete, Box, Button, CircularProgress, IconButton, TextField, Tooltip } from '@mui/material';
import {
	DataGridPremium,
	GridColDef,
	GridPaginationModel,
	GridRowSelectionModel,
	GridToolbarColumnsButton,
	GridToolbarContainer,
	GridToolbarDensitySelector,
	GridToolbarFilterButton,
	getGridDateOperators,
	getGridNumericOperators,
} from '@mui/x-data-grid-premium';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { useApolloClient, useLazyQuery, useMutation, useQuery } from '@parkchirp/api-client';
import dayjs from 'dayjs';
import { AppNames, PendingParkerActionEnum } from 'parkchirp-shared/enums';
import { FC, ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import {
	ACTIVATE_ALL_PARKERS,
	ACTIVATE_SELECTED_PARKERS,
	CHANGE_PENDING_PARKERS_START_DATE,
	DELETE_PENDING_PARKERS_BY_SUBSCRIPTION,
	DELETE_PENDING_PARKERS_BY_SUBSCRIPTION_SQS,
	SYNC_PARKER_IN_CACHE,
} from '../../mutations';
import {
	GET_PARKERS_CSV,
	GET_PREVIEW_INVOICES,
	GET_RATE_BY_ID_V2,
	LIST_ALL_PARKERS_FROM_CACHE,
	LIST_PARKERS_FROM_CACHE,
} from '../../queries';
import type { SetShowDialog, SetShowSnackbar } from '../../types';
import {
	Dayjs,
	datagridFilterPanelAttributes,
	findLabelFromValueAndOptions,
	formatCurrency,
	isStartDateInPreviousParkingPeriod,
} from '../../utils';
import { FacilityAutocomplete } from '../inputs';
import { DatagridLinearProgress } from '../loading';
import { BackBillInvoicesModal } from '../modals/backBillInvoicesModal';
import RateForm from '../rates/ratesForm';
import { CsvExportButtonV2 } from '../reports';
import {
	defaultFiltersForCache,
	defaultOrderForV2,
	onFilterModelChangeForCache,
	onSortModelChangeForCache,
} from './pendingParkersTableConfig';

type ParkersTableProps = {
	tabs?: JSX.Element;
	setShowSnackbar: SetShowSnackbar;
	setShowDialog: SetShowDialog;
	csvExportButton: ((props: any) => ReactElement) | null;
	parkerDetailModal: ((props: any) => ReactElement) | null;
	tenantSubscriptionId?: string;
	theme: any;
};

const style = {
	background: 'none',
	border: 0,
	color: 'blue',
	fontSize: 'inherit',
	cursor: 'pointer',
};

const numericOperators = getGridNumericOperators();
const dateOperators = getGridDateOperators();

const getAdditionalDataForId = (parkerIds, parkerRows) =>
	parkerIds.map((id) => ({
		id,
		subscriptionId: parkerRows.find((pr) => pr.id === id)?.subscriptionId,
	}));

export const PendingParkersTable: FC<ParkersTableProps> = ({
	setShowSnackbar,
	setShowDialog,
	csvExportButton,
	parkerDetailModal,
	tabs = null,
	tenantSubscriptionId = null,
	theme,
}) => {
	const [deletePendingParkersBySubscription, { loading: deleteLoading }] = useMutation(
		DELETE_PENDING_PARKERS_BY_SUBSCRIPTION,
	);
	const [deletePendingParkersBySubscriptionSqs, { loading: deleteLoadingSqs }] = useMutation(
		DELETE_PENDING_PARKERS_BY_SUBSCRIPTION_SQS,
	);
	const [getPreviewInvoices] = useLazyQuery(GET_PREVIEW_INVOICES, {
		fetchPolicy: 'network-only',
	});

	const [activateSelectedParkers, { loading: activateSelectedParkersLoading }] =
		useMutation(ACTIVATE_SELECTED_PARKERS);
	const [activateAllParkers, { loading: activateAllParkersLoading }] = useMutation(ACTIVATE_ALL_PARKERS);

	const [
		changePendingParkersStartDateBySubscription,
		{ loading: changePendingParkersStartDateBySubscriptionLoading },
	] = useMutation(CHANGE_PENDING_PARKERS_START_DATE);
	const bulkParkerActionOptions = Object.keys(PendingParkerActionEnum).map((key) => ({
		value: key,
		label: PendingParkerActionEnum[key as keyof typeof PendingParkerActionEnum],
	}));

	const [{ pageSize, page }, setPaginationModel] = useState<GridPaginationModel>({ pageSize: 100, page: 0 });
	const [bulkParkerActionName, setBulkParkerActionName] = useState<any>(null);
	const [bulkParkerAction, setBulkParkerAction] = useState<string | null>(null);
	const [selectedParkerIds, setSelectedParkerIds] = useState<GridRowSelectionModel>([]);
	const [showSpinner, setShowSpinner] = useState(false);
	const [rowCount, setRowCount] = useState<number>(0);

	const [syncingRowId, setSyncingRowId] = useState<string>('');
	const [syncParkerInCache] = useMutation(SYNC_PARKER_IN_CACHE);
	const [cacheSortOrder, setCacheSortOrder] = useState(defaultOrderForV2);
	const [cacheFilters, setCacheFilters] = useState(defaultFiltersForCache);
	const debouncedSetCacheFilters = useDebounce(setCacheFilters, 500);
	const [parkersWithErrors, setParkersWithErrors] = useState([]);
	const [newStartDate, setNewStartDate] = useState<Dayjs | null>(null);
	const [activateParkersFacility, setActivateParkersFacility] = useState<{
		id?: string;
		facilityName?: string;
		facilityNumber?: string;
	}>({});

	const [getAllParkers, { loading: getAllParkersLoading }] = useLazyQuery(LIST_ALL_PARKERS_FROM_CACHE, {
		variables: {
			tenantSubscriptionId,
			filters: cacheFilters,
		},
		context: {
			apiVersion: 'v2',
		},
		fetchPolicy: 'network-only', // Bypass Apollo's automated caching
	});

	const {
		data: parkersDataFromCache,
		loading: parkerCacheLoading,
		refetch: refetchParkersFromCache,
	} = useQuery(LIST_PARKERS_FROM_CACHE, {
		variables: {
			tenantSubscriptionId,
			limit: pageSize,
			offset: page * pageSize,
			order: cacheSortOrder,
			filters: cacheFilters,
		},
		context: {
			apiVersion: 'v2',
		},
		fetchPolicy: 'network-only', // Bypass Apollo's automated caching
	});

	useEffect(() => {
		if (!parkerCacheLoading && parkersDataFromCache) {
			setRowCount(parkersDataFromCache?.listParkersFromCache?.count);
		}
	}, [parkerCacheLoading, parkersDataFromCache]);
	useEffect(() => {
		setRowCount((prevRowCountState) => (rowCount !== undefined ? rowCount : prevRowCountState));
	}, [rowCount]);

	const intervalRef = useRef(null);

	const stopInterval = () => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
	};

	useEffect(() => {
		const isUpdatingCheck = () => {
			const parkers = parkersDataFromCache?.listParkersFromCache?.parkers;
			if (parkers) {
				const allUpdated = parkers.every((parker) => parker.isUpdating === false);
				if (allUpdated) {
					stopInterval();
				} else {
					refetchParkersFromCache();
				}
			}
		};
		if (parkersDataFromCache) {
			intervalRef.current = setInterval(isUpdatingCheck, 10000);

			return stopInterval;
		}
	}, [parkersDataFromCache, refetchParkersFromCache]);

	const client = useApolloClient();

	async function getRateForm(rateId: string): Promise<ReactElement> {
		const rateResponse = await client.query({
			query: GET_RATE_BY_ID_V2,
			variables: {
				rateId,
			},
			context: {
				apiVersion: 'v2',
			},
			fetchPolicy: 'network-only',
		});
		const rate = rateResponse.data.getRateDetailsById;
		return (
			<RateForm
				selectedRate={rate}
				isChild={rate.parentRateId}
				setShowDialog={setShowDialog}
				setShowSnackbar={setShowSnackbar}
				queryVariables={{
					limit: pageSize,
					offset: page * pageSize,
					order: {
						model: 'PARKER',
						field: 'created_at',
						direction: 'DESC',
					},
					filters: [],
				}}
				refetchParkers={refetchParkersFromCache}
				theme={theme}
			/>
		);
	}

	const parkerRows = parkersDataFromCache?.listParkersFromCache?.parkers;

	const handleSyncInCache = async (parkerId) => {
		setSyncingRowId(parkerId);
		try {
			await syncParkerInCache({
				variables: {
					parkerId,
				},
				context: { apiVersion: 'v2' },
			});
			setShowSnackbar({
				message: 'Successfully Synced Parker In Cache',
				severity: 'success',
				autoHideDuration: 6000,
			});
			await refetchParkersFromCache();
		} catch {
			setShowSnackbar({
				message: 'There was an error syncing this parker in the cache',
				severity: 'error',
				autoHideDuration: 6000,
			});
		} finally {
			setSyncingRowId('');
		}
	};

	const columns: GridColDef[] = [
		{
			field: 'actions',
			headerName: 'Actions',
			sortable: false,
			filterable: false,
			groupable: false,
			renderCell: (params) => {
				if (syncingRowId === params.row.id) {
					return <CircularProgress />;
				}
				if (params.row.isUpdating) {
					return (
						<Tooltip title='Parker is being updated currently'>
							<InfoIcon />
						</Tooltip>
					);
				}
				return (
					<Tooltip title='Sync parker in cache'>
						<IconButton
							disabled={!!syncingRowId && syncingRowId !== params.row.id}
							onClick={() => handleSyncInCache(params.row.id)}
						>
							<SyncIcon />
						</IconButton>
					</Tooltip>
				);
			},
		},
		{
			field: 'lastName',
			headerName: 'Last Name',
			groupable: false,
			renderCell: (params) => (
				<a
					style={style}
					onClick={() => {
						if (!parkerDetailModal) return;
						setShowDialog(
							parkerDetailModal({
								parkerId: params.row.id,
								parkerFirstName: params.row.firstName,
								parkerLastName: params.row.lastName,
								parkerAccountId: params.row.subscriptionAccountId,
								parkerSubscriptionName: params.row.subscriptionName,
								facilityId: params.row.facilityId,
								subscriptionId: params.row.subscriptionId,
								parkerFacilityName: params.row.facilityName,
								parkerFacilityNumber: params.row.facilityNumber,
								handleClose: () => {
									refetchParkersFromCache();
									setShowDialog(null);
								},
							}),
						);
					}}
					data-cy='last-name-link'
				>
					{params.row.lastName || 'Change Me'}
				</a>
			),
		},
		{
			field: 'firstName',
			headerName: 'First Name',
			groupable: false,
			renderCell: (params) => (
				<a
					style={style}
					onClick={() => {
						if (!parkerDetailModal) return;
						setShowDialog(
							parkerDetailModal({
								parkerId: params.row.id,
								parkerFirstName: params.row.firstName,
								parkerLastName: params.row.lastName,
								parkerAccountId: params.row.subscriptionAccountId,
								parkerSubscriptionName: params.row.subscriptionName,
								facilityId: params.row.facilityId,
								subscriptionId: params.row.subscriptionId,
								parkerFacilityName: params.row.facilityName,
								parkerFacilityNumber: params.row.facilityNumber,
								handleClose: () => {
									refetchParkersFromCache();
									setShowDialog(null);
								},
							}),
						);
					}}
					data-cy='first-name-link'
				>
					{params.row.firstName || 'Change Me'}
				</a>
			),
		},
		{
			field: 'suspensionStatus',
			headerName: 'Suspension Status',
			type: 'singleSelect',
			groupable: false,
			width: 200,
			valueOptions: [
				{ value: 'Suspension_Pending', label: 'Suspension Pending' },
				{ value: 'Suspended', label: 'Suspended' },
				{ value: 'Pending', label: 'Pending' },
				{ value: 'Not_Invoiced', label: 'Not Invoiced' },
				{ value: 'Active', label: 'Active' },
				{ value: 'Terminated', label: 'Terminated' },
			],
		},
		{
			field: 'sublot',
			headerName: 'Sublot Name',
			groupable: false,
			renderCell: (params) => params.row.sublot?.name || params.row.sublot || null,
		},
		{
			field: 'facilityName',
			headerName: 'Facility Name',
			groupable: false,
		},
		{
			field: 'facilityNumber',
			headerName: 'Facility Number',
			groupable: false,
			filterOperators: numericOperators,
		},
		{
			field: 'terminationStatus',
			headerName: 'Termination Status',
			width: 200,
			type: 'singleSelect',
			groupable: false,
			valueOptions: [
				{ value: 'Termination_Pending', label: 'Termination Pending' },
				{ value: 'Terminated', label: 'Terminated' },
				{ value: 'Pending', label: 'Pending' },
				{ value: 'Not_Invoiced', label: 'Not Invoiced' },
				{ value: 'Active', label: 'Active' },
			],
		},
		{
			field: 'subscriptionName',
			headerName: 'Subscription Name',
			groupable: false,
		},
		{
			field: 'accountId',
			headerName: 'Account ID',
			filterOperators: numericOperators,
			groupable: false,
			renderCell: (params) => `AC-${params.row.accountId}`,
		},
		{
			field: 'readableId',
			headerName: 'Sub. ID',
			filterOperators: numericOperators,
			groupable: false,
		},
		{
			field: 'payorType',
			headerName: 'Payor Type',
			groupable: false,
		},
		{
			field: 'rateName',
			headerName: 'Rate Name',
			groupable: false,
			...(!tenantSubscriptionId && {
				renderCell: (params) => (
					<a
						style={style}
						onClick={async () => {
							const { rateId } = params.row;
							if (rateId) {
								return setShowDialog(await getRateForm(rateId));
							}
							return null;
						}}
						data-cy='rate-name-link'
					>
						{params.row.rateName}
					</a>
				),
			}),
		},
		{
			field: 'discountType',
			headerName: 'Child Discount Type',
			type: 'singleSelect',
			groupable: false,
			valueOptions: [
				{ value: 'DOLLAR', label: 'Dollar' },
				{ value: 'PERCENT', label: 'Percent' },
				{ value: 'FEE', label: 'Fee' },
			],
		},
		{
			field: 'discountAmount',
			headerName: 'Child Discount Amount',
			filterable: false,
			groupable: false,
			renderCell: (params) => {
				if (params.row.discountType === 'PERCENT') {
					return `${params.row.discountAmount / 10000}%`;
				}
				if (params.row.discountType === 'DOLLAR') {
					return formatCurrency(params.row.discountAmount / 100);
				}
				return null;
			},
		},
		{
			field: 'net',
			headerName: 'Current Monthly Net Rate',
			groupable: false,
			filterOperators: numericOperators,
			renderCell: (params) => formatCurrency(params.row.net / 100),
		},
		{
			field: 'gross',
			headerName: 'Current Monthly Gross Rate',
			groupable: false,
			filterOperators: numericOperators,
			renderCell: (params) => formatCurrency(params.row.gross / 100),
		},
		{
			field: 'passType',
			headerName: 'Pass Type',
			groupable: false,
		},
		{
			field: 'location',
			headerName: 'Location',
			groupable: false,
		},
		{
			field: 'group',
			headerName: 'Group',
			groupable: false,
		},
		{
			field: 'accessHours',
			headerName: 'Access Hours',
			groupable: false,
			type: 'singleSelect',
			valueOptions: [
				{ value: 'Twenty_Four_Seven_Access', label: '24/7 Access' },
				{ value: 'Nights_and_Weekends', label: 'Nights and Weekends' },
				{ value: 'Monday_through_Friday_Weekday_Daytime', label: 'M-F Weekday Daytime' },
			],
		},
		{
			field: 'reservedSpace',
			headerName: 'Reserved Space',
			groupable: false,
		},
		{
			field: 'email',
			headerName: 'Email',
			groupable: false,
		},
		{
			field: 'phone',
			headerName: 'Phone',
			groupable: false,
		},
		{
			field: 'address',
			headerName: 'Address',
			groupable: false,
		},
		{
			field: 'city',
			headerName: 'City',
			groupable: false,
		},
		{
			field: 'state',
			headerName: 'State',
			groupable: false,
		},
		{
			field: 'zip',
			headerName: 'Zip',
			groupable: false,
		},
		{
			field: 'parkerStartDate',
			headerName: 'Parker Start Date',
			groupable: false,
			filterOperators: dateOperators,
		},
		{
			field: 'parkerTerminationDate',
			headerName: 'Termination Date',
			filterOperators: dateOperators,
			groupable: false,
			renderCell: (params) =>
				params.row.parkerTerminationDate
					? dayjs.utc(params.row.parkerTerminationDate).format('YYYY-MM-DD')
					: null,
		},
	];

	const isLoading =
		getAllParkersLoading ||
		deleteLoading ||
		activateSelectedParkersLoading ||
		activateAllParkersLoading ||
		deleteLoadingSqs ||
		changePendingParkersStartDateBySubscriptionLoading;

	const getGroupedBySubscriptionId = (selectedIds, selectedRows) => {
		const parkersWithSubId = getAdditionalDataForId(selectedIds, selectedRows);
		const groupedBySubscriptionId = parkersWithSubId.reduce((acc, obj) => {
			if (!acc[obj.subscriptionId]) {
				acc[obj.subscriptionId] = [];
			}
			acc[obj.subscriptionId].push(obj.id);
			return acc;
		}, {});
		return groupedBySubscriptionId;
	};

	const handleActivateOrDelete = useCallback(
		async (action, parkerData, actionName, createBackBillInvoices = false) => {
			const groupedData = getGroupedBySubscriptionId(
				parkerData.map(({ id }) => id),
				parkerData,
			);
			const promises = Object.entries(groupedData).map(async ([subscriptionId, parkerIds]) => {
				let variables: any = {
					subscriptionId,
					parkerIds,
					tenantSubscriptionId,
				};
				if (actionName.includes('activate')) {
					variables = {
						...variables,
						createBackBillInvoices,
					};
				}
				if (actionName.includes('change')) {
					variables = {
						...variables,
						startDate: newStartDate,
					};
				}
				return action({
					context: {
						apiVersion: 'v2',
					},
					variables,
				});
			});

			const results = await Promise.allSettled(promises);

			const flatMap = results.flatMap((r: any) => {
				const { parkersWithErrors: errorParkers } = r.value.data[actionName];
				const { errorMessage = '' } = r.value.data[actionName];

				return errorParkers.map((parker: any) => ({
					...parker,
					errorMessage,
				}));
			});

			return flatMap;
		},
		[newStartDate, tenantSubscriptionId],
	);

	const handleActivateOrDeleteSqs = useCallback(
		async (action, parkerData, actionName, createBackBillInvoices = false) => {
			const groupedData = getGroupedBySubscriptionId(
				parkerData.map(({ id }) => id),
				parkerData,
			);
			const promises = Object.entries(groupedData).map(async ([subscriptionId]) => {
				let variables: any = {
					subscriptionId,
					tenantSubscriptionId,
				};
				if (actionName.includes('activate')) {
					variables = {
						...variables,
						createBackBillInvoices,
					};
				}
				return action({
					context: {
						apiVersion: 'v2',
					},
					variables,
				});
			});

			return Promise.allSettled(promises);
		},
		[tenantSubscriptionId],
	);

	const handleActionSubmitClick = useCallback(
		async (allPendingParkers, createBackBillInvoices = false) => {
			let resultMessage = `Bulk action "${bulkParkerActionName}" successful.`;
			let severity: AlertColor = 'success';
			let errors = [];

			let parkerData = allPendingParkers;
			if (!parkerData) {
				if (bulkParkerAction.includes('ALL') && bulkParkerAction !== 'ACTIVATE_ALL_PENDING_PARKERS') {
					const { data } = await getAllParkers();
					parkerData = data?.listAllParkersFromCache?.parkers;
				} else {
					parkerData = parkerRows.filter(({ id }) => selectedParkerIds.includes(id));
				}
			}

			try {
				setShowDialog(null);
				setShowSpinner(true);

				if (bulkParkerAction === 'ACTIVATE_PENDING_PARKERS') {
					const { data } = await activateSelectedParkers({
						context: {
							apiVersion: 'v2',
							appName: process.env.NEXT_PUBLIC_APP_NAME,
						},
						variables: {
							parkerIds: selectedParkerIds,
							createBackBillInvoices,
						},
					});
					const { errors: activationErrors } = data.activateSelectedParkers;

					errors = activationErrors.flatMap((activationError) => {
						let { errorMessage } = activationError;
						if (!errorMessage && activationError.parkersAfterLastStartDate?.length) {
							errorMessage = `Some parker's start dates were after the facility's last available billing start date`;
						}

						return [
							...(activationError.failedParkers.map((parkerId) => ({
								parkerId,
								errorMessage,
							})) ?? []),
							...(activationError.parkersAfterLastStartDate?.map((parkerId) => ({
								parkerId,
								errorMessage,
							})) ?? []),
						];
					});

					resultMessage = errors.length
						? 'Some of these parkers could not be activated'
						: 'Successfully activated pending parkers';
					severity = errors.length ? 'warning' : 'success';
				} else if (bulkParkerAction === 'ACTIVATE_ALL_PENDING_PARKERS') {
					await activateAllParkers({
						context: {
							apiVersion: 'v2',
							appName: process.env.NEXT_PUBLIC_APP_NAME,
						},
						variables: { facilityId: activateParkersFacility.id, createBackBillInvoices },
					});

					resultMessage = `
                    Started activating all pending parkers please wait for these changes to take place.
                    You will receive an email regarding any problematic parkers
                `;

					severity = 'info';
				} else if (bulkParkerAction === 'DELETE_PENDING_PARKERS') {
					errors = await handleActivateOrDelete(
						deletePendingParkersBySubscription,
						parkerData,
						'deletePendingParkersBySubscription',
					);

					resultMessage = errors.length
						? 'Some of these parkers could not be deleted'
						: 'Successfully deleted pending parkers';

					severity = errors.length ? 'warning' : 'success';
				} else if (bulkParkerAction === 'DELETE_ALL_PENDING_PARKERS') {
					const results = await handleActivateOrDeleteSqs(
						deletePendingParkersBySubscriptionSqs,
						parkerData,
						'deletePendingParkersBySubscriptionSqs',
					);

					const hasErrors = results.some((r) => r.status === 'rejected');
					const allErrored = results.every((r) => r.status === 'rejected');

					resultMessage = hasErrors
						? 'There was an issue deleting some pending parkers'
						: allErrored
							? 'There was an issue deleting parkers'
							: `Started deleting all pending parkers please wait for these changes to take place.
                        You will receive an email regarding any problematic parkers`;
					severity = hasErrors ? 'warning' : allErrored ? 'error' : 'info';
				} else if (
					bulkParkerAction === 'GET_PREVIEW_INVOICES' ||
					bulkParkerAction === 'GET_ALL_PREVIEW_INVOICES'
				) {
					await getPreviewInvoices({
						context: { apiVersion: 'v2' },
						variables: {
							parkerIds: parkerData.map(({ id }) => id),
						},
						onCompleted: ({ getPreviewInvoices: { url } }) => {
							const csv = document.createElement('a');
							csv.href = url;
							csv.click();
						},
					});
				} else if (bulkParkerAction === 'CHANGE_PENDING_PARKER_START_DATE') {
					errors = await handleActivateOrDelete(
						changePendingParkersStartDateBySubscription,
						parkerData,
						'changePendingParkersStartDateBySubscription',
					);

					resultMessage = errors.length
						? 'Some of these parkers could not have their start date changed'
						: 'Successfully changed start date for selected pending parkers';
					severity = errors.length ? 'warning' : 'success';
				}
			} catch (e) {
				resultMessage = `Unable to ${bulkParkerActionName}: ${e.message}`;
				severity = 'error';
			} finally {
				const errorParkerIds = errors.filter((e) => e.parkerId).map((err) => err.parkerId);
				const errorMessage = errors.length > 0 && errors[0].errorMessage ? errors[0].errorMessage : '';
				setSelectedParkerIds([]);
				setBulkParkerAction(null);
				setBulkParkerActionName(null);
				setShowSpinner(false);
				refetchParkersFromCache();
				setParkersWithErrors([...errorParkerIds]);
				setNewStartDate(null);
				setShowSnackbar({
					message: `${resultMessage}${errorMessage ? `: ${errorMessage}` : errorMessage}`,
					severity,
				});
			}
		},
		[
			activateAllParkers,
			activateParkersFacility.id,
			activateSelectedParkers,
			bulkParkerAction,
			bulkParkerActionName,
			changePendingParkersStartDateBySubscription,
			deletePendingParkersBySubscription,
			deletePendingParkersBySubscriptionSqs,
			getAllParkers,
			getPreviewInvoices,
			handleActivateOrDelete,
			handleActivateOrDeleteSqs,
			parkerRows,
			refetchParkersFromCache,
			selectedParkerIds,
			setShowDialog,
			setShowSnackbar,
		],
	);

	const triggerShowBackBillModal = useCallback(async () => {
		let showBackBillModal;
		let allPendingParkerIds;
		let allPendingParkerData;
		let facilityIds: string[];
		if (bulkParkerAction === 'ACTIVATE_ALL_PENDING_PARKERS') {
			const { data } = await getAllParkers();
			allPendingParkerIds = data?.listAllParkersFromCache?.parkers.map(({ id }) => id);
			allPendingParkerData = data.listAllParkersFromCache.parkers;
			showBackBillModal = isStartDateInPreviousParkingPeriod(allPendingParkerData, allPendingParkerIds);
			facilityIds = allPendingParkerData.map(({ facilityId }) => facilityId);
		} else {
			showBackBillModal = isStartDateInPreviousParkingPeriod(parkerRows, selectedParkerIds);
			facilityIds = parkerRows
				.filter(({ id }) => selectedParkerIds.includes(id))
				.map(({ facilityId }) => facilityId);
		}
		if (showBackBillModal) {
			setShowDialog(
				<BackBillInvoicesModal
					facilityIds={facilityIds}
					closeModal={() => setShowDialog(null)}
					handleDismiss={async () => {
						await handleActionSubmitClick(allPendingParkerData, false);
					}}
					handleConfirm={async () => {
						await handleActionSubmitClick(allPendingParkerData, true);
					}}
					{...(tenantSubscriptionId && { appName: AppNames.TENANT_PORTAL })}
				/>,
			);
			return;
		}
		await handleActionSubmitClick(allPendingParkerData, false);
	}, [
		bulkParkerAction,
		getAllParkers,
		handleActionSubmitClick,
		parkerRows,
		selectedParkerIds,
		setShowDialog,
		activateParkersFacility.id,
	]);

	const renderCorrectInputsFromAction = useCallback(() => {
		if (showSpinner) {
			return <CircularProgress color='primary' />;
		}
		if (bulkParkerAction === 'ACTIVATE_PENDING_PARKERS') {
			return (
				<Button
					disabled={!parkerRows?.length || !selectedParkerIds.length || isLoading}
					onClick={triggerShowBackBillModal}
					variant='contained'
					data-cy='activate-parkers-button'
					style={{ height: '35px' }}
				>
					Activate Parkers
				</Button>
			);
		}
		if (bulkParkerAction === 'ACTIVATE_ALL_PENDING_PARKERS') {
			return (
				<>
					<Box display='flex' width='25em' mt='1em' mr='1em'>
						<FacilityAutocomplete
							facilityName={activateParkersFacility.facilityName}
							facilityNumber={activateParkersFacility.facilityNumber}
							facilityId={activateParkersFacility.id}
							organizationId={0}
							onChange={(selectedFacility) => {
								setActivateParkersFacility({
									id: selectedFacility?.id ?? '',
									facilityName: selectedFacility?.facilityName ?? '',
									facilityNumber: selectedFacility?.facilityNumber?.toString(),
								});

								debouncedSetCacheFilters([
									...defaultFiltersForCache,
									{
										propertyName: 'facilityId',
										op: 'equals',
										value: selectedFacility?.id,
									},
								]);
							}}
						/>
					</Box>
					<Button
						disabled={!activateParkersFacility.id || !parkerRows?.length || isLoading}
						onClick={triggerShowBackBillModal}
						variant='contained'
						data-cy='activate-all-parkers-button'
						style={{ height: '35px' }}
					>
						Activate Parkers By Facility
					</Button>
				</>
			);
		}
		if (bulkParkerAction === 'DELETE_ALL_PENDING_PARKERS') {
			return (
				<Button
					disabled={!parkerRows?.length || isLoading}
					onClick={async () => handleActionSubmitClick(null)}
					variant='contained'
					data-cy='delete-all-parkers-button'
					style={{ height: '35px' }}
				>
					Delete All Parkers
				</Button>
			);
		}
		if (bulkParkerAction === 'DELETE_PENDING_PARKERS') {
			return (
				<Button
					disabled={!parkerRows?.length || !selectedParkerIds.length || isLoading}
					onClick={async () => handleActionSubmitClick(null)}
					variant='contained'
					data-cy='delete-parkers-button'
					style={{ height: '35px' }}
				>
					Delete Parkers
				</Button>
			);
		}
		if (bulkParkerAction === 'GET_PREVIEW_INVOICES') {
			return (
				<Button
					disabled={!parkerRows?.length || !selectedParkerIds.length || isLoading}
					onClick={async () => handleActionSubmitClick(null)}
					variant='contained'
					data-cy='get-preview-invoices-button'
					style={{ height: '35px' }}
				>
					Preview Invoices
				</Button>
			);
		}
		if (bulkParkerAction === 'GET_ALL_PREVIEW_INVOICES') {
			return (
				<Button
					disabled={!parkerRows?.length || isLoading}
					onClick={async () => handleActionSubmitClick(null)}
					variant='contained'
					data-cy='get-all-preview-invoices-button'
					style={{ height: '35px' }}
				>
					Preview All Invoices
				</Button>
			);
		}
		if (bulkParkerAction === 'CHANGE_PENDING_PARKER_START_DATE') {
			return (
				<>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label='New Start Date'
							value={newStartDate}
							onChange={(newValue: Dayjs | null) => {
								const year = newValue.year();
								if (year < 1900) {
									return; // Don't set the date if the year is invalid
								}
								setNewStartDate(newValue || null);
							}}
							views={['year', 'month', 'day']}
							format='MM/DD/YYYY'
							slotProps={{
								openPickerIcon: {
									'data-cy': 'date-picker-open-icon',
								},
								textField: {
									inputProps: {
										'data-cy': 'change-parker-start-date-date-picker',
									},
									sx: {
										height: '35px',
										boxSizing: 'border-box',
										'& .MuiInputBase-root': {
											height: '35px',
										},
										'& .MuiInputLabel-root': {
											marginTop: '-10px',
										},
										'& .MuiInputLabel-shrink': {
											marginTop: '0px',
										},
									},
								},
							}}
							minDate={dayjs()}
						/>
					</LocalizationProvider>

					<Button
						onClick={() => handleActionSubmitClick(null)}
						disabled={!parkerRows?.length || !newStartDate || !selectedParkerIds.length}
						variant='contained'
						data-cy='save-button'
						style={{ height: '35px', marginLeft: '10px' }}
					>
						Save
					</Button>
				</>
			);
		}
		return null;
	}, [
		activateParkersFacility.facilityName,
		activateParkersFacility.facilityNumber,
		activateParkersFacility.id,
		bulkParkerAction,
		handleActionSubmitClick,
		isLoading,
		newStartDate,
		parkerRows?.length,
		selectedParkerIds.length,
		showSpinner,
		triggerShowBackBillModal,
	]);

	const CustomToolbar = useCallback(
		() => (
			<GridToolbarContainer>
				{!bulkParkerAction && (
					<GridToolbarFilterButton
						onResize={undefined}
						onResizeCapture={undefined}
						data-cy='filter-table-button'
					/>
				)}
				<GridToolbarColumnsButton onResize={undefined} onResizeCapture={undefined} />
				<GridToolbarDensitySelector onResize={undefined} onResizeCapture={undefined} />
				{csvExportButton ? (
					<CsvExportButtonV2
						query={GET_PARKERS_CSV}
						queryName='getParkersCSV'
						args={{
							variables: {
								limit: 10000,
								offset: 0,
								order: cacheSortOrder,
								filters: cacheFilters,
								// facilityId: facilityIdToUse,
							},
							context: {
								apiVersion: 'v2',
							},
						}}
					/>
				) : null}
				<div
					style={{
						width: '60%',
						display: 'flex',
						alignItems: 'center',
						flexWrap: 'wrap',
						justifyContent: 'flex-start',
						flexGrow: 1,
					}}
				>
					<Autocomplete
						id='facility-select'
						style={{ marginRight: '10px' }}
						options={bulkParkerActionOptions}
						key={bulkParkerActionName}
						value={bulkParkerActionName as any}
						isOptionEqualToValue={(option, value) => option.label === value}
						renderInput={(params) => (
							<TextField
								{...params}
								label='Action'
								data-cy='parker-bulk-actions-autocomplete'
								InputProps={{
									...params.InputProps,
									style: { height: '35px', width: '400px', padding: '0px 10px' },
								}}
								InputLabelProps={{
									style: {
										transform: bulkParkerActionName
											? 'translate(14px, -6px) scale(0.75)'
											: 'translate(14px, 10px) scale(1)',
										fontSize: '16px',
									},
									shrink: Boolean(bulkParkerActionName),
								}}
							/>
						)}
						onChange={(_, newValue) => {
							setBulkParkerAction(newValue?.value);
							const name = findLabelFromValueAndOptions(newValue?.value, bulkParkerActionOptions);
							setBulkParkerActionName(name);
						}}
					/>
					{renderCorrectInputsFromAction()}
				</div>
			</GridToolbarContainer>
		),
		[
			bulkParkerAction,
			bulkParkerActionName,
			bulkParkerActionOptions,
			cacheFilters,
			cacheSortOrder,
			csvExportButton,
			renderCorrectInputsFromAction,
		],
	);

	return (
		<div style={{ display: 'flex', flexDirection: 'column' }}>
			{tabs}
			<div className='pca__datagrid-sticky--headfoot' data-cy='parkers-table-container'>
				<DataGridPremium
					checkboxSelection={
						!!bulkParkerActionName &&
						bulkParkerActionName !== PendingParkerActionEnum.ACTIVATE_ALL_PENDING_PARKERS &&
						bulkParkerActionName !== PendingParkerActionEnum.DELETE_ALL_PENDING_PARKERS &&
						bulkParkerActionName !== PendingParkerActionEnum.GET_ALL_PREVIEW_INVOICES
					}
					paginationModel={{ page, pageSize }}
					onSortModelChange={(sortModel) =>
						onSortModelChangeForCache(sortModel, setPaginationModel, setCacheSortOrder)
					}
					onFilterModelChange={(filterModel) =>
						onFilterModelChangeForCache(filterModel, setPaginationModel, debouncedSetCacheFilters)
					}
					hideFooter={parkerCacheLoading}
					onPaginationModelChange={setPaginationModel}
					pagination
					rows={parkerRows || []}
					rowCount={rowCount}
					pageSizeOptions={[pageSize]}
					columns={columns}
					paginationMode='server'
					sortingMode='server'
					filterMode='server'
					loading={parkerCacheLoading}
					slots={{
						toolbar: CustomToolbar,
						loadingOverlay: DatagridLinearProgress,
					}}
					slotProps={{
						filterPanel: { ...datagridFilterPanelAttributes },
					}}
					rowSelectionModel={selectedParkerIds}
					onRowSelectionModelChange={(ids) => setSelectedParkerIds(ids)}
					disableColumnFilter={!!bulkParkerAction}
					initialState={{
						filter: {
							filterModel: {
								items: [],
							},
						},
					}}
					getRowClassName={(params) => (parkersWithErrors.includes(params.id) ? 'red-row' : '')}
					sx={{
						'& .red-row': {
							backgroundColor: 'rgba(255, 99, 71, .9) !important',
						},
						height: '100%',
					}}
				/>
			</div>
		</div>
	);
};
